import { SearchSource } from '@carvana/merch-contracts';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type SuggestionInfo from '~/contracts/common/SuggestionInfo';
import type { DeepPartial } from '~/types';

export type KeywordSuggestionContext = Partial<SuggestionInfo>;

type ContextType = {
  keywordSuggestion: KeywordSuggestionContext;
  refSource: string;
  resetKeywordSuggestion: () => void;
  searchSource: SearchSource;
  setKeywordSuggestion: React.Dispatch<
    React.SetStateAction<KeywordSuggestionContext>
  >;
  setRefSource: React.Dispatch<React.SetStateAction<string>>;
  setSearchSource: React.Dispatch<React.SetStateAction<SearchSource>>;
};

const defaultContext: ContextType = {
  keywordSuggestion: {},
  refSource: '',
  resetKeywordSuggestion() {},
  searchSource: SearchSource.InitialPageLoad,
  setKeywordSuggestion() {},
  setRefSource() {},
  setSearchSource() {}
};

const SearchEventsDataContext = createContext<ContextType>(defaultContext);

function normalize(value: string | string[]): string | undefined {
  return Array.isArray(value) ? value.at(0) : value;
}

function normalizeSearchSource(value: string | string[]): SearchSource {
  const searchSourceString = normalize(value);

  return Object.values(SearchSource).includes(
    searchSourceString as SearchSource
  )
    ? (searchSourceString as SearchSource)
    : defaultContext.searchSource;
}

export type OwnProps = React.PropsWithChildren<{
  refSource: string | string[];
  searchSource: string | string[];
  keywordSuggestion: KeywordSuggestionContext;
}>;

export function SearchEventsDataContextProvider({
  children,
  ...props
}: OwnProps) {
  const [searchSource, setSearchSource] = useState<SearchSource>(
    props.keywordSuggestion?.suggestionSource ||
      normalizeSearchSource(props.searchSource)
  );

  const [keywordSuggestion, setKeywordSuggestion] =
    useState<KeywordSuggestionContext>(props.keywordSuggestion ?? {});

  const [refSource, setRefSource] = useState<string>(
    normalize(props.refSource) || ''
  );

  const resetKeywordSuggestion = useCallback(() => {
    setKeywordSuggestion({});
    setRefSource('srp');
  }, []);

  const value = useMemo<ContextType>(
    (): ContextType => ({
      keywordSuggestion,
      refSource,
      resetKeywordSuggestion,
      searchSource,
      setKeywordSuggestion,
      setRefSource,
      setSearchSource
    }),
    [keywordSuggestion, refSource, resetKeywordSuggestion, searchSource]
  );

  return (
    <SearchEventsDataContext.Provider value={value}>
      {children}
    </SearchEventsDataContext.Provider>
  );
}

export default SearchEventsDataContext;

export const generateSearchEventsDataContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useKeywordSuggestion() {
  return useContext(SearchEventsDataContext).keywordSuggestion;
}

export function useSearchSource() {
  return useContext(SearchEventsDataContext).searchSource;
}

export function useRefSource() {
  return useContext(SearchEventsDataContext).refSource;
}
